/**
 * The two profiles of VorratsBuch (docs/18-vorratsbuch.md, Profile und Einheiten): Lebensmittel and
 * Haushalt. As with drinks, the stock is the content: 3 tins of 400 g are 1200 g, shown as
 * „3 Dosen“; an opened pack is estimated („halb“), never weighed. Each article points to a base
 * product of the shopping list's catalogue.
 */
import { CONTAINERS, sizeOf } from "./drinks.ts";
import { ALLERGENS } from "./drinks.ts";
import { HOUSEHOLD_AISLES, PANTRY_AISLES, suggestedUnit } from "./base-products.ts";
import type { BaseProduct } from "./base-products.ts";
import { profileName } from "./profile.ts";
import type { Profile, ProfileField, ProfileReason } from "./profile.ts";

/** What a pantry article is counted in (its base unit). */
export const PANTRY_UNITS = ["g", "ml", "Stk."] as const;

/**
 * What the stock of an article counted in Stk. shows in: its containers („2 Pck.“, „angebrochen“),
 * or single pieces when the pack is only the size it is bought in (eggs: 6 or 10 in a box, eaten one
 * by one; Martin, 19.10.2026). Empty: containers.
 */
export const COUNT_BY = ["Gebinde", "Stück"] as const;

/** Counted in single pieces: the content per container is only what one buys. */
export const countsPieces = (f: Record<string, unknown>) => f.unit === "Stk." && f.countBy === "Stück";

/** The content of a container as the stock counts with it; null for loose goods and for goods counted in pieces. */
export const pantrySize = (f: Record<string, unknown>): number | null => (countsPieces(f) ? null : sizeOf(f));

/** Counted in pieces from the start: eggs, and fruit and vegetables one counts (Äpfel, Gurken; `suggestedUnit`). */
export function suggestedCountBy(p: BaseProduct | undefined): "Stück" | undefined {
  if (!p) return undefined;
  return p.id === "ei" || (suggestedUnit(p) === "Stk." && !HOUSEHOLD_AISLES.includes(p.aisle)) ? "Stück" : undefined;
}

/** Containers of the pantry; „Packung“ when none is given. */
export const PANTRY_CONTAINERS = ["Packung", "Dose", "Glas", "Flasche", "Beutel", "Becher", "Tube", "Rolle", "Netz", "Schale", "Tafel", "Stück", "Box"] as const;

const FOOD_AISLE_NAMES = PANTRY_AISLES.filter((a) => !HOUSEHOLD_AISLES.includes(a.id)).map((a) => a.name);
const HOUSEHOLD_AISLE_NAMES = PANTRY_AISLES.filter((a) => HOUSEHOLD_AISLES.includes(a.id)).map((a) => a.name);

/** `example`: base products of the profile, as the form's placeholder shows them (B12). */
function pantryFields(aisles: readonly string[], extra: readonly ProfileField[], example: string): ProfileField[] {
  return [
    { key: "product", label: "Bezeichnung", type: "text", level: "article", required: true, group: "main", hint: "„Polpa“, „Passierte Tomaten mit Basilikum“" },
    { key: "brand", label: "Marke", type: "text", level: "article", filter: true, group: "main", hint: "leer, wenn egal" },
    { key: "base", label: "Grundprodukt", type: "base", level: "article", required: true, group: "main", hint: `z. B. ${example}` },
    { key: "aisle", label: "Abteilung", type: "choice", level: "article", required: true, filter: true, options: aisles, group: "main" },
    { key: "unit", label: "Zählen in", type: "choice", level: "article", required: true, options: PANTRY_UNITS, group: "main", hint: "nach der ersten Buchung fest" },
    { key: "size", label: "Inhalt je Gebinde", type: "number", level: "article", group: "main", hint: "in der Einheit oben: 400 (g), 1000 (ml), 40 (Tabs); leer: jedes Stück einzeln" },
    { key: "container", label: "Gebinde", type: "choice", level: "article", options: PANTRY_CONTAINERS, group: "main", hint: "leer = Packung" },
    { key: "countBy", label: "Bestand in", type: "choice", level: "article", options: COUNT_BY, group: "main", hint: "„Stück“ bei Eiern, Äpfeln: die Packung ist nur die Größe beim Einkauf; leer = Gebinde" },
    { key: "reorderAt", label: "Nachkaufen ab", type: "number", level: "article", group: "restock", hint: "so viele Gebinde oder weniger: auf die Nachkaufliste" },
    { key: "fillTo", label: "Auffüllen auf", type: "number", level: "article", group: "restock", hint: "so viele Gebinde sollen da sein; leer: eins mehr als „Nachkaufen ab“" },
    { key: "restockAuto", label: "Immer automatisch senden", type: "yesno", level: "article", group: "restock", hint: "mit gekoppeltem EinkaufsZettel: kommt ohne Tippen auf die Einkaufsliste, etwa Milch und Klopapier" },
    ...extra,
    { key: "note", label: "Notiz", type: "text", level: "article", group: "more" },
  ];
}

const words = { business: { one: "Betrieb", the: "den Betrieb", of: "des Betriebs" }, private: { one: "Haushalt", the: "den Haushalt", of: "des Haushalts" } };

const IN: ProfileReason[] = [
  { id: "purchase", label: "Einkauf", class: "procurement", for: "both" },
  { id: "delivery", label: "Lieferung", class: "procurement", for: "business" },
  { id: "homemade", label: "Selbst gemacht", class: "production", for: "both" },
  { id: "gift-received", label: "Geschenkt bekommen", class: "freeReceipt", for: "private" },
  { id: "opening", label: "Startbestand", class: "opening", for: "both" },
];

export const FOOD_PROFILE: Profile = {
  id: "lebensmittel",
  app: "VorratsBuch",
  vocabulary: {
    article: "Lebensmittel",
    articles: "Lebensmittel",
    unit: "Packung",
    units: "Packungen",
    unitShort: "Pck.",
    take: { business: "Entnehmen", private: "Verbrauchen" },
    taken: { business: "Entnommen", private: "Verbraucht" },
    workspace: words,
    search: "Vorrat suchen",
  },
  fields: pantryFields(FOOD_AISLE_NAMES, [
    { key: "keep", label: "Lagerung", type: "choice", level: "article", options: ["trocken", "kühl", "tiefgekühlt"], group: "more" },
    { key: "organic", label: "Bio", type: "yesno", level: "article", group: "more" },
    { key: "allergens", label: "Allergene", type: "multi", level: "article", options: ALLERGENS, group: "more" },
  ], "Dosentomaten, Mehl, Milch"),
  groups: { main: "Angaben", restock: "Nachkaufen", more: "Mehr" },
  nameFields: ["brand", "product"],
  categoryField: "aisle",
  packs: [],
  expires: true,
  reasons: [
    ...IN,
    { id: "used", label: "Verbraucht", class: "consumption", for: "private" },
    { id: "kitchen", label: "Küche", class: "consumption", for: "business" },
    { id: "cooked", label: "Gekocht", class: "consumption", for: "both" },
    { id: "spoiled", label: "Abgelaufen", class: "knownLoss", for: "both" },
    { id: "discarded", label: "Weggeworfen", class: "knownLoss", for: "both" },
    { id: "gift", label: "Verschenkt", class: "freeIssue", for: "both" },
  ],
  tracking: "lot",
  countMode: "article",
  starter: [
    { id: "vorrat", name: "Vorratsschrank", locations: [["vorrat-regal", "Regal"]] },
    { id: "kuehl", name: "Kühlschrank", locations: [["kuehl-fach", "Fach"]] },
    { id: "truhe", name: "Gefriertruhe", locations: [["truhe-fach", "Fach"]] },
  ],
};

export const HOUSEHOLD_PROFILE: Profile = {
  id: "haushalt",
  app: "VorratsBuch",
  vocabulary: { ...FOOD_PROFILE.vocabulary, article: "Haushaltsartikel", articles: "Haushaltsartikel", search: "Haushaltsartikel suchen" },
  fields: pantryFields(HOUSEHOLD_AISLE_NAMES, [], "Spülmittel, Toilettenpapier"),
  groups: { main: "Angaben", restock: "Nachkaufen", more: "Mehr" },
  nameFields: ["brand", "product"],
  categoryField: "aisle",
  packs: [],
  reasons: [
    ...IN,
    { id: "used", label: "Verbraucht", class: "consumption", for: "both" },
    { id: "discarded", label: "Weggeworfen", class: "knownLoss", for: "both" },
    { id: "gift", label: "Verschenkt", class: "freeIssue", for: "both" },
  ],
  tracking: "quantity",
  countMode: "article",
  starter: [{ id: "putzschrank", name: "Haushaltsschrank", locations: [["putzschrank-fach", "Fach"]] }],
};

export const PANTRY_PROFILES: readonly Profile[] = [FOOD_PROFILE, HOUSEHOLD_PROFILE];

export const isPantry = (f: Record<string, unknown>) => f.profile === "lebensmittel" || f.profile === "haushalt";

/** „2 l“, „400 g“, „40 Stk.“: a content as shown next to a name. */
export function contentText(qty: number, unit: string): string {
  if (unit === "g" && qty >= 1000 && qty % 100 === 0) return `${String(qty / 1000).replace(".", ",")} kg`;
  if (unit === "ml" && qty >= 1000 && qty % 100 === 0) return `${String(qty / 1000).replace(".", ",")} l`;
  return `${qty} ${unit}`;
}

/** The name of a pantry article: „Mutti Polpa (400 g)“; without content, or counted in pieces („Eier Freiland“), just brand and product. */
export function pantryName(f: Record<string, unknown>): string {
  const profile = f.profile === "haushalt" ? HOUSEHOLD_PROFILE : FOOD_PROFILE;
  const name = profileName(profile, f);
  const size = pantrySize(f);
  const unit = typeof f.unit === "string" ? f.unit : "Stk.";
  return size !== null && !(unit === "Stk." && size === 1) ? `${name} (${contentText(size, unit)})` : name;
}

/**
 * A name as shown: a number keeps its unit on the same line („700 g“, „3,5 %“; B9), with a
 * no-break space. Only for showing; the stored name keeps its plain spaces.
 */
export function shownName(name: string): string {
  return name.replace(/(\d) (?=(?:kg|g|ml|l|Stk\.|%)(?=[)\s,]|$))/g, "$1\u00a0");
}

/** How full an opened container is, as one taps it in: never weighed (docs/18-vorratsbuch.md). */
export const LEVELS = [
  { id: "full", label: "voll", share: 1 },
  { id: "half", label: "halb", share: 0.5 },
  { id: "little", label: "wenig", share: 0.2 },
  { id: "empty", label: "leer", share: 0 },
] as const;
export type Level = (typeof LEVELS)[number]["id"];

/** „Angebrochene Packung“, „Angebrochener Beutel“, „Angebrochenes Glas“: the opened container by its gender. */
export function openedName(f: Record<string, unknown>): string {
  const c = typeof f.container === "string" && f.container !== "" ? f.container : "Packung";
  const ending = ["Beutel", "Becher"].includes(c) ? "er" : ["Glas", "Netz", "Stück"].includes(c) ? "es" : "e";
  return `Angebrochen${ending} ${c}`;
}

/** The content an opened container counts with at a level: half of 1000 g are 500 g. */
export function levelQty(level: Level, size: number): number {
  return Math.round(size * LEVELS.find((l) => l.id === level)!.share);
}

/**
 * A pantry stock as it shows: full containers and whether one is opened (a rest below a whole
 * container), with the rest in the base unit for the grey line („1 Pck., angebrochen“ / „350 g“).
 */
export function pantryStock(qty: number, f: Record<string, unknown>): { full: number; opened: boolean; rest: number; container: { name: string; plural: string }; unit: string } {
  const unit = typeof f.unit === "string" && f.unit !== "" ? f.unit : "Stk.";
  const size = pantrySize(f) ?? 1;
  const name = typeof f.container === "string" && f.container in CONTAINERS ? f.container : "Packung";
  const c = CONTAINERS[name]!;
  const q = Math.max(0, qty);
  const full = Math.floor(q / size);
  const rest = q - full * size;
  return { full, opened: rest > 0, rest, container: { name: c.short, plural: c.plural }, unit };
}

/** „3 Dosen“, „1 Pck., angebrochen“, „angebrochen“, „leer“; loose goods in their unit („5 Stk.“, „1,5 kg“). */
export function pantryStockText(qty: number, f: Record<string, unknown>): string {
  if (pantrySize(f) === null) return qty > 0 ? contentText(qty, typeof f.unit === "string" && f.unit !== "" ? f.unit : "Stk.") : "leer";
  const s = pantryStock(qty, f);
  const whole = s.full > 0 ? `${s.full} ${s.full === 1 ? s.container.name : s.container.plural}` : "";
  if (!s.opened) return whole || "leer";
  return whole ? `${whole}, angebrochen` : "angebrochen";
}

/**
 * A number of containers as the app writes them, „2 Pck.“, „1 Fl.“, „3 Dosen“ (B15: the shopping
 * list says „Packung“); any other unit as given.
 */
export function containersText(qty: number, unit: string): string {
  const c = CONTAINERS[unit];
  const n = qty.toLocaleString("de-DE");
  return c ? `${n} ${qty === 1 ? c.short : c.plural}` : `${n} ${unit}`;
}

/**
 * Whether a household records prices in its pantry (C4, Martin 26.10.2026): only when its settings
 * (`card/settings`) say so; the value of a pantry is off by default (docs/18-vorratsbuch.md, Profile).
 * Amounts of receipts are kept either way, so nothing is missing when it is switched on.
 */
export const pantryPricesOn = (settings: Record<string, unknown> | undefined): boolean => settings?.pantryPrices === true;

/** A loose amount: „350 g“, from 1000 on in kg or l with a comma („7,5 kg“, „1,25 l“), as the inbound shows it. */
export function amountText(qty: number, unit: string): string {
  const big = unit === "g" ? "kg" : unit === "ml" ? "l" : null;
  if (big && Math.abs(qty) >= 1000) return `${(qty / 1000).toLocaleString("de-DE", { maximumFractionDigits: 3 })} ${big}`;
  return `${qty} ${unit}`;
}

/**
 * A pantry quantity in a line of the app: whole containers and an opened one („2 Dosen + 1
 * angebrochen“), loose goods in their unit („7,5 kg“). Nothing or less than nothing (a booking the
 * stock did not cover) is „nichts“, never grams below zero (docs/auftrag-befunde-tutorials-vorratsbuch.md, A1).
 */
export function pantryQtyText(qty: number, f: Record<string, unknown>): string {
  if (qty <= 0) return "nichts";
  const unit = typeof f.unit === "string" && f.unit !== "" ? f.unit : "Stk.";
  const size = pantrySize(f);
  if (size === null || size <= 1) return amountText(qty, unit);
  const s = pantryStock(qty, f);
  const whole = s.full > 0 ? `${s.full} ${s.full === 1 ? s.container.name : s.container.plural}` : "";
  return s.opened ? [whole, "1 angebrochen"].filter(Boolean).join(" + ") : whole;
}

/**
 * The opened rest at a location when nothing whole is left there (150 g of a 500-g pack), else 0.
 * „Verbrauchen“ then suggests this rest instead of a whole pack the stock does not have (A1).
 */
export function openedRest(here: number, f: Record<string, unknown>): number {
  const size = pantrySize(f);
  return size !== null && size > 1 && here > 0 && here < size ? here : 0;
}

/**
 * What to buy again (docs/04-domaenenmodell.md, Nachschub;docs/18-vorratsbuch.md, V0-6): with a
 * rule „Nachkaufen ab“ and the stock at or below it, the containers up to „Auffüllen auf“ (one more
 * than the threshold without), rounded up to whole containers. An opened container counts as there.
 * 0 when nothing is due or there is no rule. `stock` is in the base unit, over the app's storages.
 */
export function restockNeed(stock: number, f: Record<string, unknown>): number {
  const at = typeof f.reorderAt === "number" && f.reorderAt >= 0 ? f.reorderAt : null;
  if (at === null) return 0;
  const size = pantrySize(f) ?? 1;
  const containers = Math.ceil(Math.max(0, stock) / size);
  if (containers > at) return 0;
  const to = typeof f.fillTo === "number" && f.fillTo > at ? f.fillTo : at + 1;
  return to - containers;
}
